/**
 * fleetApi.js — Backend API client
 *
 * Centralizes calls to the FleetManagement backend and maps API payloads to the
 * frontend shapes used by the current UI.
 */

import { normalizeVehicleType } from '../config/vehicleTypes';

// No fallback on purpose: a missing value must fail loudly at startup rather
// than silently sending traffic to the wrong backend. Set REACT_APP_API_BASE
// in .env (production, committed) or .env.local (development override).
const API_BASE = process.env.REACT_APP_API_BASE;
if (!API_BASE) {
  throw new Error('REACT_APP_API_BASE is not set. Add it to .env (or .env.local for local development), then restart/rebuild.');
}

// Shared auth header builder — avoids repeating the conditional everywhere
const authHeader = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

// Session-expired callback — set once by App.js after auth context is ready
let _onSessionExpired = null;
export const setSessionExpiredCallback = (fn) => { _onSessionExpired = fn; };
const handle401 = (status) => { if (status === 401 && _onSessionExpired) _onSessionExpired(); };

export class ApiError extends Error {
  constructor(message, status, path, body) {
    super(message);
    this.name   = 'ApiError';
    this.status = status;
    this.path   = path;
    this.body   = body;
  }
}

const parseErrorBody = async (res) => {
  const text = await res.text();
  try { return text ? JSON.parse(text) : null; } catch { return null; }
};

const getJson = async (path, token) => {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: authHeader(token),
  });
  if (!res.ok) {
    handle401(res.status);
    const body = await parseErrorBody(res);
    throw new ApiError(`${res.status}`, res.status, path, body);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

const deleteJson = async (path, token) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
  });
  if (!res.ok) {
    handle401(res.status);
    const body = await parseErrorBody(res);
    throw new ApiError(`${res.status}`, res.status, path, body);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

const postJson = async (path, body, token, timeoutMs = 30000, { skipSessionCheck = false } = {}) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader(token),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw new ApiError('timeout', 0, path, null);
    throw err;
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    if (!skipSessionCheck) handle401(response.status);
    const errBody = await parseErrorBody(response);
    throw new ApiError(`${response.status}`, response.status, path, errBody);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const makeInternalId = (prefix, existing) => existing || `${prefix}-${Date.now()}`;

// Coerce an optional numeric form value to a number, or null when blank.
// Form inputs surface numbers as strings (or '' when cleared) — never send ''.
const numOrNull = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

/* <input type="datetime-local"> hands back a naive local wall-clock with no
   zone on it — "2026-09-05T14:30" — and passing that through unchanged made
   the backend read 14:30 Beirut as 14:30 UTC, so every time the user typed
   drifted by their offset.

   The trap is that the server's own timestamps are ALSO zone-less: System.Text
   .Json writes DateTime with no offset, so a stored value comes back as
   "2020-01-15T00:00:00". Treating every zone-less string as local therefore
   re-converted the server's own values on their way back out — and since most
   date fields here are merely round-tripped by a save that was not even about
   them, they shifted by the offset on EVERY save, cumulatively. Three saves in
   Lagos moved a licence expiry back three hours.

   The two cases are distinguishable by shape, and only by shape: a
   datetime-local control emits minute precision and no seconds, while the
   serialiser always writes seconds. So convert only the minute-precision form
   and leave everything else exactly as received.

   Note that pages are free to convert at the input boundary instead, which is
   the more robust place for it — Orders.js already does, via fromLocalInput. */
const LOCAL_DATETIME_INPUT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

const toIsoDateTime = (value) => {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  if (!value.includes('T')) return `${value}T00:00:00Z`;
  if (!LOCAL_DATETIME_INPUT.test(value)) return value;   // server value — hands off
  const d = new Date(value);                             // parsed as local, which is right
  return Number.isNaN(d.getTime()) ? value : d.toISOString();
};

// ─── Status Enums (id ↔ name) ─────────────────────────────────────────────────
const ORDER_STATUS       = { 1: 'scheduled', 2: 'dispatched', 3: 'enroute', 4: 'completed', 5: 'cancelled' };
const ORDER_STATUS_ID    = { scheduled: 1, dispatched: 2, enroute: 3, completed: 4, cancelled: 5 };

const VEHICLE_STATUS     = { 1: 'active', 2: 'inactive', 3: 'enroute', 4: 'maintenance' };
const VEHICLE_STATUS_ID  = { active: 1, inactive: 2, enroute: 3, maintenance: 4 };

const TXN_STATUS         = { 1: 'pending', 2: 'completed' };
const TXN_STATUS_ID      = { pending: 1, completed: 2 };

const TXN_TYPE           = { 1: 'fuel', 2: 'tax', 3: 'order', 4: 'maintenance' };
const TXN_TYPE_ID        = { fuel: 1, tax: 2, order: 3, maintenance: 4 };

/* eRepairStatus is Open=1, InProgress=2, Completed=3, CANCELLED=4. Leaving 4
   out meant REPAIR_STATUS[4] was undefined, fell through the `|| 'pending'`
   fallback, and a cancelled job displayed as pending — and then the reverse
   map's `|| 1` wrote it back as OPEN. Touch any field on a cancelled repair
   and the cancellation was destroyed. */
const REPAIR_STATUS    = { 1: 'pending', 2: 'in-progress', 3: 'completed', 4: 'cancelled' };
const REPAIR_STATUS_ID = { pending: 1, 'in-progress': 2, completed: 3, cancelled: 4 };

// Deviation thresholds (%) at which each fuel report raises an anomaly. The two
// reports do NOT share a meaning: tank-to-tank flags at 20% over expected,
// distance-based estimate at 40%. Backend computes the flags; these constants
// are for labelling the badges on the frontend only.
export const FUEL_ANOMALY = { TANK_TO_TANK: 20, ESTIMATE: 40 };

const mapContactFromApi = (item) => ({
  id: item.id,
  name: item.name || '',
  type: item.type || 'Customer',
  email: item.email || '',
  phone: item.phone || '',
  altPhone: item.altPhone || '',
  companyName: item.companyName || '',
  positionTitle: item.positionTitle || '',
  notes: item.notes || '',
  status: item.status || 'active',
  organizationId: item.organizationId ?? 1,
  vendorId: item.vendorId || '',
  placeId: item.placeId || '',
  /* Read it so it can be written back. The controller assigns
     PhotoDocumentId on every update, so a DTO that omits it nulls the
     column — and a field this mapper does not read cannot round-trip. */
  photoDocumentId: item.photoDocumentId || null,
});

/* No `internalId` on contacts, drivers, fleets, places, vehicles, vendors,
   organizations, zones, geofences, fuel or repairs — the column was dropped
   server-side (migration RemoveInternalIds, 19 Sep 2026). Orders, sell orders
   and transactions keep theirs; those mappers still carry it. */
const mapContactToApi = (item, organizationId) => ({
  id: item.id || 0,
  name: item.name || '',
  type: item.type || 'Customer',
  email: item.email || null,
  phone: item.phone || null,
  altPhone: item.altPhone || null,
  companyName: item.companyName || null,
  positionTitle: item.positionTitle || null,
  notes: item.notes || null,
  status: item.status || 'active',
  // The record's own organization wins; the caller's id is the fallback for a
  // create. Overwriting it on update moved a record between organizations.
  organizationId: item.organizationId ?? organizationId,
  /* numOrNull, not `|| null`. A grid dropdown builds its options as
     `String(id)` and the cell keeps whatever the option carried, so picking a
     vendor puts the STRING "3" in the record. Every form on this page coerces
     with `+e.target.value`; the grid is the one path that did not, and every
     other foreign key in this file is already numOrNull'd. Bulk edit makes it
     forty rows at a time. */
  vendorId: numOrNull(item.vendorId),
  placeId: numOrNull(item.placeId),
  /* Carried, not dropped. Every write path on Contacts — grid cell, bulk set,
     paste, the form — plus both of Vendors' contact mirrors went through here,
     and the controller does `PhotoDocumentId = body.PhotoDocumentId != 0 ? … :
     null`, so an omitted field nulled the column on every save. */
  photoDocumentId: item.photoDocumentId || null,
});

const mapDriverFromApi = (item) => ({
  id: item.id,
  fleetId: item.fleetId || '',
  name: item.name || '',
  phone: item.phone || '',
  email: item.email || '',
  driverLicenseNumber: item.driverLicenseNumber || '',
  driverLicenseExpiry: item.driverLicenseExpiry || '',
  status: item.status || 'active',
  userId: item.userId || null,
  contactId: item.contactId || null,
  organizationId: item.organizationId ?? 1,
  photoDocumentId: item.photoDocumentId || null,
  /* DriverController.Update does `driver.AvatarUrl = body.AvatarUrl`, and this
     pair never carried the field — so every driver save nulled it. Nothing in
     the UI reads it yet, which is exactly why it went unnoticed. */
  avatarUrl: item.avatarUrl || null,
  documentIds: Array.isArray(item.documentIds) ? item.documentIds : [],
  documents: Array.isArray(item.documents) ? item.documents : [],
  missingDocsCount: item.missingDocsCount ?? null,
  employmentDate: item.employmentDate || null,
  bvnNumber:      item.bvnNumber      || null,
  guarantorPhone: item.guarantorPhone  || null,
});

const mapDriverToApi = (item, organizationId) => ({
  id: item.id || 0,
  // numOrNull: the grid's Fleet dropdown carries String(id). See mapContactToApi.
  fleetId: numOrNull(item.fleetId),
  name: item.name || '',
  phone: item.phone || null,
  email: item.email || null,
  driverLicenseNumber: item.driverLicenseNumber || null,
  driverLicenseExpiry: toIsoDateTime(item.driverLicenseExpiry),
  status: item.status || 'active',
  userId: item.userId || null,
  contactId: item.contactId || null,
  // The record's own organization wins. The caller's id is the fallback for
  // a create. Overwriting it on update moved a driver between organizations
  // on any edit made while `currentUser.organizationId` disagreed.
  organizationId: item.organizationId ?? organizationId,
  avatarUrl: item.avatarUrl || null,
  photoDocumentId: item.photoDocumentId || null,
  documentIds: Array.isArray(item.documentIds) ? item.documentIds : [],
  employmentDate: toIsoDateTime(item.employmentDate),
  bvnNumber:      item.bvnNumber      || null,
  guarantorPhone: item.guarantorPhone  || null,
});

/* No `vendorId`. The link was reversed on 19 Sep 2026 (migration
   MoveVendorFleetLink): a vendor now belongs to a fleet, not the other way
   round. See `fleetId` on mapVendorFromApi. */
const mapFleetFromApi = (item) => ({
  id: item.id,
  name: item.name || '',
  code: item.code || '',
  description: item.description || '',
  status: item.status || 'active',
  zoneId: item.zoneId || '',
  organizationId: item.organizationId ?? 1,
  /* Kept so mapFleetToApi can send them back. Nothing in the UI edits either
     field; they are here only so a save round-trips the stored value rather
     than inventing one. See the note on mapFleetToApi below. */
  metadata: item.metadata || '',
  photoDocumentId: item.photoDocumentId || null,
});

const mapFleetToApi = (item, organizationId) => ({
  id: item.id || 0,
  name: item.name || '',
  code: item.code || null,
  description: item.description || null,
  status: item.status || 'active',
  zoneId: item.zoneId ? Number(item.zoneId) : null,
  organizationId: item.organizationId ?? organizationId,
  /* `Fleet/update` ASSIGNS these from the request body — `fleet.Metadata =
     body.Metadata` and `fleet.PhotoDocumentId = body.PhotoDocumentId != 0 ?
     ... : null` — rather than leaving the column alone when the property is
     absent. A mapper that omits them therefore sends undefined, the body
     deserialises them as null, and every save writes NULL over Fleet.Metadata
     and Fleet.PhotoDocumentId. No screen shows either value, which is exactly
     why the loss went unnoticed. */
  metadata: item.metadata || null,
  photoDocumentId: item.photoDocumentId || null,
});

const mapCoordinate = (coordinate) => coordinate ? ({
  id: coordinate.id || 0,
  latitude: coordinate.latitude,
  longitude: coordinate.longitude,
}) : null;

const mapPlaceFromApi = (item) => ({
  id: item.id,
  name: item.name || '',
  type: item.type || 'Depot',
  contactId: item.contactId || '',
  addressLine1: item.addressLine1 || '',
  addressLine2: item.addressLine2 || '',
  city: item.city || '',
  state: item.state || '',
  postalCode: item.postalCode || '',
  country: item.country || '',
  phone: item.phone || '',
  email: item.email || '',
  notes: item.notes || '',
  vendorId: item.vendorId || '',
  organizationId: item.organizationId ?? 1,
  /* Read back so mapPlaceToApi has something to write back. None of these four
     has a field on any form or a column in any grid — they are carried purely
     so a save round-trips the stored value instead of inventing one. See the
     note on mapPlaceToApi below for what happens when they are dropped. */
  timezone: item.timezone || '',
  contactName: item.contactName || '',
  metadata: item.metadata || '',
  photoDocumentId: item.photoDocumentId || null,
  gpsLatitude:  item.latitude  ?? null,
  gpsLongitude: item.longitude ?? null,
});

const mapPlaceToApi = (item, organizationId) => ({
  id: item.id || 0,
  name: item.name || '',
  type: item.type || 'Depot',
  // numOrNull: the grid's Contact dropdown carries String(id). See
  // mapContactToApi.
  contactId: numOrNull(item.contactId),
  addressLine1: item.addressLine1 || null,
  addressLine2: item.addressLine2 || null,
  city: item.city || null,
  state: item.state || null,
  postalCode: item.postalCode || null,
  country: item.country || null,
  phone: item.phone || null,
  email: item.email || null,
  notes: item.notes || null,
  vendorId: item.vendorId || null,
  organizationId: item.organizationId ?? organizationId,
  /* `Place/update` ASSIGNS all four from the request body — `place.Timezone =
     body.Timezone`, `place.ContactName = body.ContactName`, `place.Metadata =
     body.Metadata`, `place.PhotoDocumentId = body.PhotoDocumentId != 0 ? ... :
     null` — it does not skip a property that the body omits. So a mapper that
     leaves them out sends undefined, the body deserialises them as null, and
     every single save writes NULL over Place.Timezone, Place.ContactName,
     Place.Metadata and Place.PhotoDocumentId. Vendors.js saves the linked
     place on every vendor edit, so this fired on records nobody was editing. */
  timezone: item.timezone || null,
  contactName: item.contactName || null,
  metadata: item.metadata || null,
  photoDocumentId: item.photoDocumentId || null,
  latitude:  (item.gpsLatitude != null && item.gpsLatitude !== '') ? Number(item.gpsLatitude) : null,
  longitude: (item.gpsLongitude != null && item.gpsLongitude !== '') ? Number(item.gpsLongitude) : null,
});

const mapVehicleFromApi = (item) => ({
  id: item.id,
  name: item.name || '',
  plateNumber: item.plateNumber || '',
  vin: item.vin || '',
  type: normalizeVehicleType(item.type) || item.type || '',
  make: item.make || '',
  model: item.model || '',
  year: item.year || '',
  color: item.color || '',
  capacity: item.capacity || '',
  status: VEHICLE_STATUS[item.statusId] || 'active',
  currentOdometer: item.currentOdometer || 0,
  expectedConsumption: item.expectedConsumption ?? null,
  tankCapacity:        item.tankCapacity        ?? null,
  fleetId: item.fleetId || '',
  fuelType: item.fuelType || '',
  odometerOffset: item.odometerOffset ?? null,
  vendorId: item.vendorId || '',
  organizationId: item.organizationId ?? 1,
  coordinatesId: item.coordinatesId || item.coordinate?.id || '',
  coordinates: item.coordinates || item.coordinate || null,
  photoDocumentId: item.photoDocumentId || null,
  driverId:                  item.driverId ? Number(item.driverId) : null,
  trackerUniqueId:           item.trackerUniqueId           || '',
  trackerPassword:           item.trackerPassword           || '',
  trackerPhoneNumber:        item.trackerPhoneNumber        || '',
  trackerNumberRechargeDate: item.trackerNumberRechargeDate || '',
  documentIds: Array.isArray(item.documentIds) ? item.documentIds : [],
  documents: Array.isArray(item.documents) ? item.documents : [],
  missingDocsCount: item.missingDocsCount ?? null,
});

const mapVehicleTrackerFromApi = (item) => ({
  vehicleId:          item.vehicleId,
  vehicleName:        item.vehicleName        || '',
  vehicleNumber:      item.vehicleNumber       || '',
  trackerUniqueId:    item.trackerUniqueId     || '',
  trackerPhoneNumber: item.trackerPhoneNumber  || '',
  trackerPassword:    item.trackerPassword     || '',
  expirationDate:     item.expirationDate      || null,
  organizationId:     item.organizationId      ?? null,
  organizationName:   item.organizationName    || '',
});

const mapVehicleToApi = (item, organizationId) => ({
  id: item.id || 0,
  name: item.name || null,
  plateNumber: item.plateNumber || null,
  vin: item.vin || null,
  type: item.type || null,
  make: item.make || null,
  model: item.model || null,
  year: item.year || null,
  color: item.color || null,
  capacity: item.capacity || null,
  statusId: VEHICLE_STATUS_ID[item.status] || null,
  currentOdometer: item.currentOdometer || 0,
  /* Both of these are READ by mapVehicleFromApi and were never written back.
     `Vehicle/update` replaces the record, so every save through the grid or
     the wizard silently unassigned the driver — the vehicle lost its seat,
     the Drivers board gained a phantom vacancy — and reset the odometer
     offset the fuel report depends on. */
  driverId:            numOrNull(item.driverId),
  odometerOffset:      numOrNull(item.odometerOffset),
  expectedConsumption: numOrNull(item.expectedConsumption),
  tankCapacity:        numOrNull(item.tankCapacity),
  // numOrNull: the grid's Fleet and Vendor dropdowns carry String(id), and
  // this mapper coerced every OTHER foreign key but these two. See
  // mapContactToApi for the full reasoning.
  fleetId: numOrNull(item.fleetId),
  fuelType: item.fuelType || null,
  vendorId: numOrNull(item.vendorId),
  // The record's own organization wins; the caller's is the create fallback.
  organizationId: item.organizationId ?? organizationId,
  coordinatesId: item.coordinatesId || item.coordinates?.id || null,
  coordinates: mapCoordinate(item.coordinates),
  photoDocumentId: item.photoDocumentId || null,
  trackerUniqueId:           item.trackerUniqueId           || null,
  trackerPassword:           item.trackerPassword           || null,
  trackerPhoneNumber:        item.trackerPhoneNumber        || null,
  trackerNumberRechargeDate: toIsoDateTime(item.trackerNumberRechargeDate),
  documentIds: Array.isArray(item.documentIds) ? item.documentIds : [],
});

const mapViewFromApi = (item) => ({
  id:            item.id,
  title:         item.title || '',
  url:           item.url || '',
  parentId:      item.parentId || null,
  orderSequence: item.orderSequence ?? 0,
  isWebOnly:     !!item.isWebOnly,
});
const mapViewToApi = (item) => ({
  id:            item.id || 0,
  title:         item.title || '',
  url:           item.url || '',
  parentId:      item.parentId ? Number(item.parentId) : null,
  orderSequence: item.orderSequence ? Number(item.orderSequence) : 0,
  isWebOnly:     !!item.isWebOnly,
});

const mapOrganizationFromApi = (item) => ({
  id:          item.id,
  name:        item.name        || '',
  description: item.description || '',
  isDefault:   !!item.isDefault,
  userId:      item.userId      || 0,
  zones:       Array.isArray(item.zones) ? item.zones : [],
});

const mapOrganizationToApi = (item, userId) => ({
  id:          item.id          || 0,
  userId:      userId           || item.userId || 0,
  name:        item.name        || '',
  description: item.description || '',
  isDefault:   !!item.isDefault,
  zones:       Array.isArray(item.zones)
    ? item.zones.map(z => ({ id: z.id || 0, description: z.description || z.name || '' }))
    : [],
});

/**
 * Normalizes a user list row. The id arrives under different names depending on
 * the endpoint (`userId` from getAllUsers, `id` elsewhere), so it is resolved
 * once here rather than at each call site.
 */
const mapUserFromApi = (item) => ({
  id:                item.userId ?? item.id ?? 0,
  firstName:         item.firstName || '',
  lastName:          item.lastName  || '',
  fullName:          `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.username || '',
  username:          item.username || '',
  email:             item.email    || '',
  companyName:       item.companyName       || '',
  phoneNumber:       item.phoneNumber       || '',
  mobilePhoneNumber: item.mobilePhoneNumber || '',
  statusId:          item.statusId       ?? null,
  organizationId:    item.organizationId ?? null,
  organizationName:  item.organizationName || '',
});

const mapTemplateFromApi = (item) => ({
  id:          item.id,
  name:        item.name        || '',
  description: item.description || '',
  isDefault:   !!item.isDefault,
  customerId:  item.customerId  || '',
  vehicleId:   item.vehicleId   || '',
  driverId:    item.driverId    || '',
  currency:    item.currency    || '',
  notes:       item.notes       || '',
  userId:      item.userId      || 0,
  items: Array.isArray(item.items)
    ? item.items.map(li => ({
        itemId:       String(li.itemId || ''),
        name:         li.name         || '',
        quantity:     li.quantity     ?? 1,
        price:        li.price        ?? 0,
        currencyCode: li.currencyCode || item.currency || '',
      }))
    : [],
});

const mapTemplateToApi = (item) => ({
  id:          item.id   || 0,
  name:        item.name || '',
  description: item.description || '',
  isDefault:   !!item.isDefault,
  customerId:  item.customerId ? Number(item.customerId) : 0,
  vehicleId:   item.vehicleId  ? Number(item.vehicleId)  : 0,
  driverId:    item.driverId   ? Number(item.driverId)   : 0,
  currency:    item.currency   || '',
  notes:       item.notes      || '',
  userId:      item.userId     || 0,
  items: (item.items || []).map(li => ({
    itemId:       Number(li.itemId)      || 0,
    name:         li.name               || '',
    quantity:     Number(li.quantity)   || 1,
    price:        parseFloat(li.price)  || 0,
    currencyCode: li.currencyCode       || item.currency || '',
  })),
});

const mapVendorFromApi = (item) => ({
  id: item.id,
  name: item.name || '',
  type: item.type || 'Transport',
  email: item.email || '',
  phone: item.phone || '',
  website: item.website || '',
  status: item.status || 'active',
  /* The fleet this vendor serves. One fleet at most; several vendors may
     point at the same fleet. Replaces the old Fleet.vendorId (19 Sep 2026). */
  fleetId: item.fleetId || '',
  /* No `vendorRegistration`. There is no such property on VendorData, no such
     column on the Vendor table, and VendorController never reads or writes
     one — so this only ever mapped `undefined` to '' and handed the UI a field
     that could be typed into but never stored. */
  /* Kept so mapVendorToApi can send it back; nothing in the UI edits it. */
  billingAddress: item.billingAddress || '',
  placeId: item.placeId || '',
  contactId: item.contactId || '',
  organizationId: item.organizationId ?? 1,
  gpsLatitude:  item.latitude  ?? null,
  gpsLongitude: item.longitude ?? null,
});

const mapVendorToApi = (item, organizationId) => ({
  id: item.id || 0,
  name: item.name || '',
  type: item.type || null,
  email: item.email || null,
  phone: item.phone || null,
  website: item.website || null,
  status: item.status || 'active',
  // numOrNull: the grid's Fleet dropdown carries String(id). See mapContactToApi.
  // The controller treats 0 and null the same ("no fleet").
  fleetId: numOrNull(item.fleetId),
  /* `Vendor/update` ASSIGNS this from the request body —
     `vendor.BillingAddress = body.BillingAddress` — rather than leaving the
     column alone when the property is absent. Omitting it sent undefined, the
     body deserialised it as null, and every save wiped Vendor.BillingAddress.
     (Vendor.Metadata is NOT in this list on purpose: VendorController sets it
     to string.Empty itself and never looks at the body.) */
  billingAddress: item.billingAddress || null,
  placeId: item.placeId || null,
  contactId: item.contactId || null,
  organizationId: item.organizationId ?? organizationId,
  latitude:  (item.gpsLatitude != null && item.gpsLatitude !== '') ? Number(item.gpsLatitude) : null,
  longitude: (item.gpsLongitude != null && item.gpsLongitude !== '') ? Number(item.gpsLongitude) : null,
});

const mapCustomerFromApi = (item) => ({
  id: item.id,
  name: item.fullName || item.name || '',
  email: item.email || '',
  phone: item.phoneNumber || item.phone || '',
  altPhone: item.mobilePhoneNumber || item.altPhone || '',
  companyName: item.companyName || '',
  notes: item.notes || '',
  status: item.status || 'active',
  organizationId: item.organizationId ?? 1,
  placeId: item.placeId || '',
  contactId: item.contactId || '',
  photoDocumentId: item.photoDocumentId || null,
  gpsLatitude:  item.latitude  ?? null,
  gpsLongitude: item.longitude ?? null,
});

const mapCustomerToApi = (item, organizationId) => ({
  id: item.id || 0,
  fullName: item.name || '',
  companyName: item.companyName || null,
  status: item.status || 'active',
  email: item.email || null,
  phoneNumber: item.phone || null,
  mobilePhoneNumber: item.altPhone || null,
  organizationId: item.organizationId ?? organizationId,
  placeId:   item.placeId   ? Number(item.placeId)   : 0,
  contactId: item.contactId ? Number(item.contactId) : 0,
  photoDocumentId: item.photoDocumentId || null,
  notes: item.notes || null,
  latitude:  (item.gpsLatitude != null && item.gpsLatitude !== '') ? Number(item.gpsLatitude) : null,
  longitude: (item.gpsLongitude != null && item.gpsLongitude !== '') ? Number(item.gpsLongitude) : null,
});

const mapOrderFromApi = (item) => ({
  id: item.id,
  internalId: item.internalId,
  originPlaceId: item.originPlaceId,
  destinationPlaceId: item.destinationPlaceId,
  contactId: item.contactId || '',
  trackingNumber: item.trackingNumber || '',
  publicId: item.publicId || '',
  vehicleId: item.vehicleId || '',
  driverId: item.driverId || '',
  type: item.type || 'Standard',
  status: ORDER_STATUS[item.statusId] || 'scheduled',
  scheduledAt: item.scheduledAt || '',
  dispatchedAt: item.dispatchedAt || '',
  startedAt: item.startedAt || '',
  completedAt: item.completedAt || '',
  cancelledAt: item.cancelledAt || '',
  currency: item.currency || 'NGN',
  totalAmount: item.totalAmount || '',
  podMethod: item.podMethod || '',
  payload: item.payload || '',
  notes: item.notes || '',
  vendorId: item.vendorId,
  customerId: item.customerId,
  expectedArrivalTime: item.expectedArrivalTime || '',
  organizationId: item.organizationId || '',
});

const mapOrderToApi = (item, organizationId) => ({
  id: item.id || 0,
  internalId: makeInternalId('ORD', item.internalId),
  originPlaceId:      item.originPlaceId      ? Number(item.originPlaceId)      : 0,
  destinationPlaceId: item.destinationPlaceId ? Number(item.destinationPlaceId) : 0,
  contactId:  item.contactId  ? Number(item.contactId)  : 0,
  vehicleId:  item.vehicleId  ? Number(item.vehicleId)  : 0,
  driverId:   item.driverId   ? Number(item.driverId)   : 0,
  trackingNumber: item.trackingNumber || null,
  publicId:   item.publicId   || null,
  type:       item.type       || null,
  statusId:   ORDER_STATUS_ID[item.status] || null,
  scheduledAt:  toIsoDateTime(item.scheduledAt),
  dispatchedAt: toIsoDateTime(item.dispatchedAt),
  startedAt:    toIsoDateTime(item.startedAt),
  completedAt:  toIsoDateTime(item.completedAt),
  cancelledAt:  toIsoDateTime(item.cancelledAt),
  currency:    item.currency    || 'NGN',
  totalAmount: item.totalAmount || 0,
  podMethod:   item.podMethod   || null,
  payload:     item.payload     || null,
  notes:       item.notes       || null,
  vendorId:    item.vendorId    ? Number(item.vendorId)    : 0,
  customerId:  item.customerId  ? Number(item.customerId)  : 0,
  expectedArrivalTime: item.expectedArrivalTime ? new Date(item.expectedArrivalTime).toISOString() : null,
  /* THE RECORD'S OWN ORGANIZATION WINS — the same rule every other mapper in
     this file states and follows. `organizationId || 0` sent 0 whenever the
     caller's id was unresolved, and OrderController.Update writes
     `OrganizationId = body.OrganizationId != 0 ? body.OrganizationId : null`.
     One save during that window set the column NULL and the order vanished
     from GetListByOrganizationId permanently. */
  organizationId: item.organizationId ?? organizationId ?? 0,
});

const mapTransactionFromApi = (item) => ({
  id: item.id,
  internalId: item.internalId,
  userId: item.userId ?? null,
  orderId: item.orderId,
  organizationId: item.organizationId || 0,
  customerId: item.customerId || 0,
  amount: item.amount || 0,
  currency: item.currency || 'NGN',
  status: TXN_STATUS[item.statusId] || 'pending',
  orderStatus: item.orderStatus || '',
  completedAt: item.completedAt || '',
  goods:       item.goods       || '',
  type:        TXN_TYPE[item.typeId] || '',
  vehicleId:   item.vehicleId        || '',
  receiptDocumentId: item.receiptDocumentId || null,
});

const mapTransactionToApi = (item, organizationId) => ({
  id: item.id || 0,
  /* `internalId` is the column this grid is sorted, searched and exported by,
     and the confirm dialogs name transactions with it — and it was not in this
     body at all, so Transaction/update nulled it on every write. Marking one
     transaction paid erased the identifier it was known by. */
  internalId: item.internalId || null,
  orderId: item.orderId ? Number(item.orderId) : 0,
  /* TransactionController.Update assigns UserId from the body; omitting it
     nulled "who booked this" on every save. */
  userId: item.userId ? Number(item.userId) : null,
  // Record's own organization first — see mapOrderToApi.
  organizationId: item.organizationId ?? organizationId ?? 0,
  customerId: item.customerId ? Number(item.customerId) : 0,
  amount: item.amount || 0,
  currency: item.currency || 'NGN',
  statusId: TXN_STATUS_ID[item.status] || null,
  completedAt: toIsoDateTime(item.completedAt),
  goods:       item.goods || null,
  typeId:      TXN_TYPE_ID[item.type] || null,
  vehicleId:   item.vehicleId ? Number(item.vehicleId) : null,
  receiptDocumentId: item.receiptDocumentId || null,
});

const mapUserLoginDetailsToUser = (loginData, fallback = {}) => ({
  id: loginData?.id || fallback.id || 0,
  fullName: `${loginData?.user?.firstName || fallback.fullName?.split(' ')[0] || ''} ${loginData?.user?.lastName || fallback.fullName?.split(' ').slice(1).join(' ') || ''}`.trim() || fallback.fullName || loginData?.username || 'User',
  email: loginData?.email || fallback.email || '',
  username: loginData?.username || fallback.username || '',
  phone: loginData?.user?.mobilePhoneNumber || loginData?.user?.phoneNumber || fallback.phone || '',
  role: fallback.role || fallback.roleName || 'dispatcher',
  department: fallback.department || 'Operations',
  status: fallback.status || 'active',
  organizationId: fallback.organizationId ?? 1,
  companyName: loginData?.user?.companyName || fallback.companyName || '',
});

const mapItemFromApi = (item) => ({
  id:             item.id,
  name:           item.name           || '',
  description:    item.description    || '',
  documentId:     item.documentId     || null,
  price:          item.price          ?? 0,
  currencyCode:   item.currencyCode   || 'NGN',
  quantity:       item.quantity       ?? 0,
  organizationId: item.organizationId || 0,
  documents: Array.isArray(item.documents) ? item.documents : [],
});

const mapItemToApi = (item, organizationId) => ({
  id:             item.id             || 0,
  name:           item.name           || null,
  description:    item.description    || null,
  documentId:     item.documentId     || null,
  price:          item.price          ?? 0,
  currencyCode:   item.currencyCode   || null,
  quantity:       item.quantity       ?? 0,
  /* The record's own organization wins; the caller's id is the fallback for a
     create — the same rule every other mapper in this file states. Forcing
     the caller's id meant a save with an unresolved currentUser.organizationId
     moved the item into org 1, where it vanished from the list it was saved
     from. */
  organizationId: item.organizationId ?? organizationId,
});

const mapSellOrderFromApi = (item) => ({
  id:              item.id,
  internalId:      item.internalId      || '',
  contactId:       item.contactId       || '',
  customerId:      item.customerId      || '',
  vehicleId:       item.vehicleId       || '',
  driverId:        item.driverId        || '',
  orderTemplateId: item.orderTemplateId || '',
  type:            item.type            || '',
  status:          ORDER_STATUS[item.statusId] || 'scheduled',
  completedAt:     item.completedAt     || '',
  currency:        item.currency        || 'NGN',
  totalAmount:     item.totalAmount     ?? 0,
  notes:           item.notes           || '',
  userId:          item.userId          || null,
  organizationId:  item.organizationId  || 0,
  items: Array.isArray(item.items) ? item.items : [],
  documents: Array.isArray(item.documents) ? item.documents : [],
});

const mapSellOrderToApi = (item, organizationId) => ({
  id:              item.id              || 0,
  internalId:      item.id ? (item.internalId || null) : null,
  contactId:       item.contactId       ? Number(item.contactId)       : null,
  customerId:      item.customerId      ? Number(item.customerId)      : null,
  vehicleId:       item.vehicleId       ? Number(item.vehicleId)       : null,
  driverId:        item.driverId        ? Number(item.driverId)        : null,
  orderTemplateId: item.orderTemplateId ? Number(item.orderTemplateId) : null,
  type:            item.type            || null,
  /* A blank or unrecognised status must not be a state CHANGE. This defaulted
     to enroute, so clearing the status select sent a van out on the road. */
  statusId:        ORDER_STATUS_ID[item.status] || ORDER_STATUS_ID['scheduled'],
  currency:        item.currency        || null,
  totalAmount:     item.totalAmount     ?? null,
  notes:           item.notes           || null,
  completedAt:     toIsoDateTime(item.completedAt),
  userId:          item.userId          || null,
  organizationId:  item.organizationId ?? organizationId ?? 0,
  items: Array.isArray(item.items)
    ? item.items.map(i => ({
        itemId:       Number(i.itemId),
        quantity:     Number(i.quantity),
        name:         i.name         || null,
        description:  i.description  || null,
        documentId:   i.documentId   || null,
        price:        i.price        ?? null,
        currencyCode: i.currencyCode || null,
      }))
    : [],
});

/* THE DTO IS { Id, Code, OrganizationId, Description, RateToUsd, IsDefault }.
   This pair was written against fields that do not exist on it — `name`,
   `isBase`, `isActive` — so `name` and `isBase` always read back empty/false
   (the UI never knew which currency was the base), and on the way out the
   `[Required] Description` was missing entirely. CurrencyController carries
   [ApiController], so model validation rejected the body and EVERY currency
   update returned 400. NotificationContext swallows it with `.catch(() => {})`,
   which is why saving currency settings has silently done nothing.
   `isBase`/`isActive` are kept as the app's own vocabulary and mapped onto the
   one field the server actually has. */
const mapCurrencyFromApi = (item) => ({
  id:         item.id,
  code:       item.code        || '',
  name:       item.description || item.name || '',
  rateToUsd:  item.rateToUsd   ?? 1,
  isBase:     item.isDefault   ?? item.isBase ?? false,
  isActive:   true,
  organizationId: item.organizationId || 0,
});

const mapCurrencyToApi = (item, organizationId) => ({
  id:           item.id         || 0,
  code:         item.code       || '',
  // [Required, MaxLength(255)] on the server — an empty string fails validation,
  // so fall back to the code rather than sending nothing.
  description:  item.name || item.description || item.code || '',
  rateToUsd:    item.rateToUsd  ?? 1,
  isDefault:    item.isBase     ?? false,
  organizationId: item.organizationId ?? organizationId ?? 0,
});

const RESOURCE_TYPE_TO_INT = { Vehicle: 1, vehicle: 1, '1': 1, Driver: 2, driver: 2, '2': 2 };
const normalizeResourceType = (v) =>
  typeof v === 'number' ? v : (RESOURCE_TYPE_TO_INT[v] ?? null);

const mapDefaultResourceDocFromApi = (item) => {
  const resourceType = normalizeResourceType(item.resourceType);
  return {
    id:                item.id                ?? 0,
    resourceType,
    resourceTypeLabel: resourceType === 2 ? 'Driver' : 'Vehicle',
    documentName:      item.documentName      || item.name || '',
    documentTypeName:  item.documentTypeName  || '',
    documentTypeId:    item.documentTypeId    ?? null,
    organizationId:    item.organizationId    ?? null,
    description:       item.description       || '',
    hasExpiration:     !!item.hasExpiration,
    allowNotification: !!item.allowNotification,
  };
};

const mapDefaultResourceDocToApi = (item) => ({
  id:                item.id                || 0,
  resourceType:      item.resourceType,
  documentName:      item.documentName      || '',
  documentTypeId:    item.documentTypeId    || null,
  organizationId:    item.organizationId    || null,
  description:       item.description       || '',
  hasExpiration:     !!item.hasExpiration,
  allowNotification: !!item.allowNotification,
});

const mapResourceDocFromApi = (item) => ({
  id:                         item.resourceDocumentId         ?? item.id ?? 0,
  resourceId:                 item.resourceId                 ?? null,
  resourceType:               item.resourceType               ?? null,
  documentName:               item.documentName               || '',
  documentTypeId:             item.documentTypeId             ?? null,
  documentTypeName:           item.documentTypeName           || '',
  description:                item.description                || '',
  hasExpiration:              !!item.hasExpiration,
  expirationDate:             item.expiryDate                 || item.expirationDate || null,
  documentId:                 item.documentId                 || null,
  allowNotification:          !!item.allowNotification,
  isCustom:                   !!item.isCustom,
  defaultResourceDocumentId:  item.defaultResourceDocumentId  ?? null,
});

const mapResourceDocToApi = (item) => ({
  resourceDocumentId:         item.id                         || 0,
  resourceId:                 item.resourceId                 || 0,
  resourceType:               item.resourceType,
  documentName:               item.documentName               || '',
  documentTypeId:             item.documentTypeId             || null,
  documentTypeName:           item.documentTypeName           || '',
  description:                item.description                || '',
  hasExpiration:              !!item.hasExpiration,
  expiryDate:                 toIsoDateTime(item.expirationDate),
  documentId:                 item.documentId                 || null,
  allowNotification:          !!item.allowNotification,
  isCustom:                   !!item.isCustom,
  defaultResourceDocumentId:  item.defaultResourceDocumentId  || null,
});

const mapDocumentTypeFromApi = (item) => ({
  id:           item.id           ?? 0,
  description:  item.description  || '',
  isFixed:      !!item.isFixed,
  resourceType: item.resourceType || null,
});

const mapDocumentTypeToApi = (item) => ({
  id:           item.id || 0,
  description:  item.description || '',
  isFixed:      !!item.isFixed,
  resourceType: item.resourceType || null,
});

const toArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (Array.isArray(v.$values)) return v.$values;
  if (Array.isArray(v.data))    return v.data;
  if (Array.isArray(v.value))   return v.value;
  if (Array.isArray(v.result))  return v.result;
  if (process.env.NODE_ENV !== 'production') console.warn('[toArr] Unexpected response shape:', v);
  return [];
};

// ── Geofences ────────────────────────────────────────────────────────────────
// Field names are kept identical to the API's on both sides of the mapper. That
// is what makes createCrud's `update` safe here: update answers with an empty
// body, so it falls back to `fromApi(item)` — re-mapping what we just sent —
// and a rename would make that fallback produce a differently-shaped object
// than a real response.

const mapGeofenceFromApi = (item) => ({
  id:             item.id,
  name:           item.name || '',
  organizationId: item.organizationId ?? 1,
  // Anything unrecognised reads as a circle: it is the shape that survives a
  // missing `points` array, where a polygon would render as nothing at all.
  shape:          item.shape === 'Polygon' ? 'Polygon' : 'Circle',
  centerLatitude:  item.centerLatitude  ?? null,
  centerLongitude: item.centerLongitude ?? null,
  radiusMeters:    item.radiusMeters    ?? null,   // null, never 0 — 0 is an invalid radius, not "unset"
  points: toArr(item.points).map((p) => ({ latitude: Number(p.latitude), longitude: Number(p.longitude) })),
  // All three default true server-side, so an absent flag means on.
  enabled:        item.enabled        ?? true,
  notifyOnEnter:  item.notifyOnEnter  ?? true,
  notifyOnExit:   item.notifyOnExit   ?? true,
  color:          item.color || '',
  vehicleIds:     toArr(item.vehicleIds).map(Number),
  fleetIds:       toArr(item.fleetIds).map(Number),
  zoneIds:        toArr(item.zoneIds).map(Number),
  createdAt:      item.createdAt ?? null,
});

const mapGeofenceToApi = (item, organizationId) => ({
  id:             item.id || 0,
  name:           (item.name || '').trim(),
  organizationId,
  shape:          item.shape,                     // 'Circle' | 'Polygon' — case-sensitive server-side
  // Only the fields belonging to the chosen shape are sent; the others go as
  // null so a circle edited from a polygon cannot leave stale vertices behind.
  centerLatitude:  item.shape === 'Circle' ? numOrNull(item.centerLatitude)  : null,
  centerLongitude: item.shape === 'Circle' ? numOrNull(item.centerLongitude) : null,
  radiusMeters:    item.shape === 'Circle' ? numOrNull(item.radiusMeters)    : null,
  // Ring order, and the first point is NOT repeated at the end — the server
  // closes the ring itself and a duplicated vertex is a validation failure.
  points: item.shape === 'Polygon'
    ? (item.points || []).map((p) => ({ latitude: Number(p.latitude), longitude: Number(p.longitude) }))
    : [],
  enabled:       item.enabled       !== false,
  notifyOnEnter: item.notifyOnEnter !== false,
  notifyOnExit:  item.notifyOnExit  !== false,
  color:         item.color || null,
  // All three lists go on EVERY update: the server replaces what it is given
  // rather than merging, so omitting one unlinks everything in it.
  vehicleIds: (item.vehicleIds || []).map(Number),
  fleetIds:   (item.fleetIds   || []).map(Number),
  zoneIds:    (item.zoneIds    || []).map(Number),
});

const mapGeofenceEventFromApi = (item) => ({
  id:           item.id,
  geofenceId:   item.geofenceId,
  geofenceName: item.geofenceName || '',
  vehicleId:    item.vehicleId,
  vehicleName:  item.vehicleName || '',
  plateNumber:  item.plateNumber || '',
  eventType:    item.eventType === 'Exit' ? 'Exit' : 'Enter',   // the only two values
  latitude:     item.latitude  ?? null,
  longitude:    item.longitude ?? null,
  // When the tracker was there, versus when we confirmed it. Show fixTimeUtc:
  // the gap between them is confirmation lag, not a data error.
  fixTimeUtc:   item.fixTimeUtc ?? null,
  createdAt:    item.createdAt  ?? null,
});

/** `take` is capped server-side at 500; clamping here avoids a pointless 400. */
const geofenceRangeBody = (id, { from, to, skip = 0, take = 100 } = {}) => ({
  id,
  ...(from ? { from } : {}),
  ...(to   ? { to }   : {}),
  skip,
  take: Math.min(take, 500),
});

/**
 * setEnabled applies only the flags it receives, so an `undefined` must not be
 * serialised as null — that would read as "turn this off" rather than "leave
 * this alone".
 */
const onlyBooleans = (flags = {}) =>
  Object.fromEntries(Object.entries(flags).filter(([, v]) => typeof v === 'boolean'));

const mapRepairItemFromApi = (item) => ({
  id:         item.id         ?? 0,
  repairId:   item.repairId   ?? 0,
  vendorId:   item.vendorId   ?? null,
  vendorName: item.vendorName  || '',
  name:       item.name        || '',
  quantity:   item.quantity    ?? 1,
  unitPrice:  item.unitPrice   ?? 0,
  totalPrice: item.totalPrice  ?? 0,
});

const mapRepairItemToApi = (item) => ({
  id:         item.id        || 0,
  repairId:   item.repairId  || 0,
  vendorId:   item.vendorId  || null,
  name:       item.name       || '',
  quantity:   item.quantity   ?? 1,
  unitPrice:  item.unitPrice  ?? 0,
  totalPrice: (item.quantity ?? 1) * (item.unitPrice ?? 0),
});

const mapRepairFromApi = (item) => ({
  id:               item.id               ?? 0,
  vehicleId:        item.vehicleId         ?? null,
  vehicleName:      item.vehicleName        || '',
  vendorId:         item.vendorId           ?? null,
  vendorName:       item.vendorName          || '',
  title:            item.title              || '',
  description:      item.description        || '',
  status:           REPAIR_STATUS[item.statusId] || 'pending',
  odometerAtRepair: item.odometerAtRepair   ?? null,
  startDate:        item.startDate           || null,
  endDate:          item.endDate             || null,
  totalCost:        item.totalCost           ?? 0,
  items:            Array.isArray(item.items) ? item.items.map(mapRepairItemFromApi) : [],
});

const mapRepairToApi = (item) => ({
  id:               item.id               || 0,
  /* numOrNull, like every other foreign key in this file. The repair form's
     two selects write raw `e.target.value`, so these arrived as strings; it
     only ever worked because the API is configured to read numbers from
     strings, which is not a guarantee to build on. */
  vehicleId:        numOrNull(item.vehicleId) ?? 0,
  vendorId:         numOrNull(item.vendorId),
  title:            item.title              || '',
  description:      item.description        || null,
  statusId:         REPAIR_STATUS_ID[item.status] || 1,
  odometerAtRepair: item.odometerAtRepair   || null,
  startDate:        item.startDate           || null,
  endDate:          item.endDate             || null,
  items:            Array.isArray(item.items) ? item.items.map(mapRepairItemToApi) : [],
});

const mapVehicleAssetFromApi = (item) => ({
  id:               item.id               ?? 0,
  vehicleId:        item.vehicleId         ?? 0,
  assetDescription: item.assetDescription  || '',
});

const mapVehicleAssetToApi = (item) => ({
  id:               item.id               || 0,
  vehicleId:        item.vehicleId         || 0,
  assetDescription: item.assetDescription  || '',
});

// ─── Fuel (fill-ups + consumption reports) ────────────────────────────────────
// One create call saves the entry, books a linked "Fuel" transaction, and (if the
// odometer entered is higher than known) advances the vehicle's odometer — all
// server-side. The frontend never touches the transactions API for fuel.
const mapFuelEntryFromApi = (item) => ({
  id:                 item.id                 ?? 0,
  vehicleId:          item.vehicleId          ?? null,
  vehicleName:        item.vehicleName         || '',
  driverId:           item.driverId           ?? null,
  driverName:         item.driverName          || '',
  vendorId:           item.vendorId           ?? null,
  vendorName:         item.vendorName          || '',
  organizationId:     item.organizationId     ?? null,
  transactionId:      item.transactionId      ?? null,
  filledAt:           item.filledAt            || null,
  odometer:           item.odometer           ?? null,
  quantity:           item.quantity           ?? 0,
  unitPrice:          item.unitPrice          ?? null,
  totalCost:          item.totalCost          ?? null,
  currency:           item.currency            || '',
  isFullTank:         !!item.isFullTank,
  receiptDocumentId:  item.receiptDocumentId  ?? null,
  notes:              item.notes               || '',
  isOverfill:         !!item.isOverfill,
  hasOdometerMismatch: !!item.hasOdometerMismatch,
});

const mapFuelEntryToApi = (item) => ({
  id:                item.id                || 0,
  vehicleId:         item.vehicleId         || 0,
  driverId:          item.driverId          || null,
  vendorId:          item.vendorId          || null,
  filledAt:          item.filledAt ? toIsoDateTime(item.filledAt) : null,
  odometer:          item.odometer === '' || item.odometer == null ? null : Number(item.odometer),
  quantity:          Number(item.quantity) || 0,
  unitPrice:         item.unitPrice === '' || item.unitPrice == null ? null : Number(item.unitPrice),
  totalCost:         item.totalCost === '' || item.totalCost == null ? null : Number(item.totalCost),
  currency:          item.currency || null,
  isFullTank:        !!item.isFullTank,
  receiptDocumentId: item.receiptDocumentId || null,
  notes:             item.notes || null,
});

const mapFuelSegmentFromApi = (s) => ({
  vehicleId:           s.vehicleId          ?? null,
  fromDate:            s.fromDate            || null,
  toDate:              s.toDate              || null,
  fromOdometer:        s.fromOdometer       ?? 0,
  toOdometer:          s.toOdometer         ?? 0,
  distanceKm:          s.distanceKm         ?? 0,
  fuelLiters:          s.fuelLiters         ?? 0,
  totalCost:           s.totalCost          ?? null,
  consumptionPer100Km: s.consumptionPer100Km ?? 0,
  costPerKm:           s.costPerKm          ?? null,
  deviationPercent:    s.deviationPercent   ?? null,
});

const mapFuelConsumptionReportFromApi = (r) => ({
  vehicleId:               r?.vehicleId               ?? null,
  vehicleName:             r?.vehicleName               || '',
  expectedConsumption:     r?.expectedConsumption      ?? null,
  segments:                Array.isArray(r?.segments) ? r.segments.map(mapFuelSegmentFromApi) : [],
  averageConsumptionPer100Km: r?.averageConsumptionPer100Km ?? null,
  totalDistanceKm:         r?.totalDistanceKm          ?? 0,
  totalFuelLiters:         r?.totalFuelLiters          ?? 0,
  totalCost:               r?.totalCost                ?? null,
  averageCostPerKm:        r?.averageCostPerKm         ?? null,
});

const mapVehicleFuelSummaryFromApi = (item) => ({
  vehicleId:                  item.vehicleId                  ?? null,
  vehicleName:                item.vehicleName                  || '',
  plateNumber:                item.plateNumber                  || '',
  fuelType:                   item.fuelType                     || '',
  fillUpCount:                item.fillUpCount                ?? 0,
  totalFuelLiters:            item.totalFuelLiters             ?? 0,
  totalCost:                  item.totalCost                   ?? null,
  currency:                   item.currency                     || '',
  distanceKm:                 item.distanceKm                 ?? null,
  averageConsumptionPer100Km: item.averageConsumptionPer100Km  ?? null,
  expectedConsumption:        item.expectedConsumption         ?? null,
  deviationPercent:           item.deviationPercent            ?? null,
  costPerKm:                  item.costPerKm                   ?? null,
  lastFillUpDate:             item.lastFillUpDate               || null,
  lastOdometer:               item.lastOdometer                ?? null,
  hasAnomaly:                 !!item.hasAnomaly,
});

const mapFuelEstimateFromApi = (item) => ({
  vehicleId:           item.vehicleId           ?? null,
  vehicleName:         item.vehicleName           || '',
  plateNumber:         item.plateNumber           || '',
  fromDate:            item.fromDate              || null,
  toDate:              item.toDate                || null,
  startOdometerKm:     item.startOdometerKm      ?? null,
  endOdometerKm:       item.endOdometerKm        ?? null,
  distanceKm:          item.distanceKm           ?? null,
  expectedConsumption: item.expectedConsumption  ?? null,
  estimatedLiters:     item.estimatedLiters      ?? null,
  estimatedCost:       item.estimatedCost        ?? null,
  purchasedLiters:     item.purchasedLiters      ?? null,
  purchasedCost:       item.purchasedCost        ?? null,
  currency:            item.currency               || '',
  deviationPercent:    item.deviationPercent     ?? null,
  hasAnomaly:          !!item.hasAnomaly,
});

const mapOdometerLogFromApi = (item) => ({
  id:         item.id         ?? 0,
  vehicleId:  item.vehicleId  ?? null,
  readingUtc: item.readingUtc  || null,
  odometerKm: item.odometerKm ?? 0,
  source:     item.source      || '',
});

// ─── Vehicle Loads (per-vehicle inventory + transaction audit trail) ──────────
// eVehicleLoadTransactionType — the type also implies the stock direction:
// Load = boxes added onto the vehicle (+), Offload = remaining boxes returned
// to the warehouse (−), Order = sold through an order (−, system-generated).
// The RESTOCK key mirrors the backend enum name; 'Offload' is the display label.
export const VEHICLE_LOAD_TXN_TYPE = { ORDER: 1, RESTOCK: 2, LOAD: 3 };
export const VEHICLE_LOAD_TXN_TYPE_LABELS = { 1: 'Order', 2: 'Offload', 3: 'Load' };
// Types that increase the vehicle's stock; everything else decreases it.
export const VEHICLE_LOAD_INCREASE_TYPES = [VEHICLE_LOAD_TXN_TYPE.LOAD];

const mapVehicleLoadFromApi = (item) => ({
  id:          item.id,
  vehicleId:   item.vehicleId   ?? null,
  vehicleName: item.vehicleName || '',
  itemId:      item.itemId      ?? null,
  itemName:    item.itemName    || '',
  quantity:    item.quantity    ?? 0,
  createDate:  item.createDate  ?? null,
  updateDate:  item.updateDate  ?? null,
});

const mapVehicleLoadTransactionFromApi = (item) => ({
  id:                item.id,
  vehicleLoadId:     item.vehicleLoadId     ?? null,
  mode:              item.mode              ?? null, // enum meaning TBC with backend
  type:              item.type              ?? null,
  quantity:          item.quantity          ?? 0,
  resultingQuantity: item.resultingQuantity ?? null,
  createDate:        item.createDate        ?? null,
  orderId:           item.orderId           || null, // set on Order-type movements
  orderName:         item.orderName         || '',
});

// ─── Settings (Default + per-User) ────────────────────────────────────────────
// eSettingValueType — how a setting's string `value` should be interpreted
export const SETTING_VALUE_TYPE = { INT: 1, STRING: 2, RANGE: 3, BOOL: 4, COMBO: 5 };

// `value` is always a string on the wire; parse it per type.
// Combo values are option ids (see optionsJson) — kept numeric for lookups.
const parseSettingValue = (type, value) => {
  if (value == null || value === '') return null;
  switch (type) {
    case SETTING_VALUE_TYPE.INT:
    case SETTING_VALUE_TYPE.RANGE:
    case SETTING_VALUE_TYPE.COMBO:
      return Number.isNaN(Number(value)) ? value : Number(value);
    case SETTING_VALUE_TYPE.BOOL:
      return value === 'true' || value === '1';
    default:
      return value;
  }
};

const parseOptionsJson = (raw) => {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

const mapSettingFromApi = (item) => ({
  id:               item.id,
  userId:           item.userId           ?? null,
  defaultSettingId: item.defaultSettingId ?? null,
  key:              item.key  || '',
  name:             item.name || '',
  type:             item.type ?? SETTING_VALUE_TYPE.STRING,
  options:          parseOptionsJson(item.optionsJson), // e.g. [{id, name}, ...] for Combo
  value:            parseSettingValue(item.type, item.value),
  rawValue:         item.value ?? null,
  createDate:       item.createDate ?? null,
  updateDate:       item.updateDate ?? null,
});

const mapDefaultSettingToApi = (item) => ({
  ...(item.id ? { id: item.id } : {}),
  ...(item.id ? {} : { key: item.key || '' }), // key is set on create, immutable on edit
  name:        item.name || '',
  type:        item.type ?? SETTING_VALUE_TYPE.STRING,
  optionsJson: item.options != null ? JSON.stringify(item.options) : (item.optionsJson || null),
  value:       item.value != null ? String(item.value) : '',
});

// ─── Plans & Subscriptions ───────────────────────────────────────────────────
// maxVehicles is null when the plan is unlimited — never 0. Keep the `?? null`
// and numOrNull forms below: `Number(x) || null` would collapse a real 0 into
// null, and a 0 that reached the server would lock an org out of every vehicle.

// No mapMyPlanToApi — GetMyPlan takes no body; the org comes from the token.
const mapMyPlanFromApi = (item) => ({
  hasActiveSubscription: !!item?.hasActiveSubscription,
  isExpired:             !!item?.isExpired,
  planCode:              item?.planCode || '',
  planName:              item?.planName || '',
  expiryDateUtc:         item?.expiryDateUtc || null,
  maxVehicles:           item?.maxVehicles ?? null,
  vehicleCount:          item?.vehicleCount ?? 0,
  // Storage quota lives on the plan, not the subscription. null = unlimited,
  // never 0 — same rule as maxVehicles. storageUsedBytes is always a number.
  maxStorageMb:          item?.maxStorageMb ?? null,
  storageUsedBytes:      item?.storageUsedBytes ?? 0,
});

const mapPlanFromApi = (item) => ({
  id:              item.id ?? 0,
  code:            item.code || '',
  name:            item.name || '',
  description:     item.description || '',
  isActive:        !!item.isActive,
  sortOrder:       item.sortOrder ?? 0,
  maxStorageMb:    item.maxStorageMb ?? null,
  viewCount:       item.viewCount ?? 0,
  subscriberCount: item.subscriberCount ?? 0,
});

// viewCount / subscriberCount are server-computed aggregations — never sent.
// plans/upsert is a FULL REPLACE: every field omitted here is blanked on the
// server, so maxStorageMb must be sent on every save or editing a plan's name
// would silently reset its quota to unlimited.
const mapPlanToApi = (item) => ({
  id:           item.id || 0,
  code:         (item.code || '').trim(),
  name:         (item.name || '').trim(),
  description:  item.description || '',
  isActive:     !!item.isActive,
  sortOrder:    numOrNull(item.sortOrder) ?? 0,
  maxStorageMb: numOrNull(item.maxStorageMb),
});

const mapSubscriptionFromApi = (item) => ({
  id:               item.id ?? 0,
  organizationId:   item.organizationId ?? null,
  organizationName: item.organizationName || '',
  planId:           item.planId ?? null,
  planName:         item.planName || '',
  status:           item.status || '',
  startDateUtc:     item.startDateUtc || null,
  expiryDateUtc:    item.expiryDateUtc || null,
  maxVehicles:      item.maxVehicles ?? null,
  vehicleCount:     item.vehicleCount ?? 0,
  isExpired:        !!item.isExpired,
  notes:            item.notes || '',
});

const mapSubscriptionToApi = (item) => ({
  id:             item.id || 0,
  organizationId: numOrNull(item.organizationId),
  planId:         numOrNull(item.planId),
  status:         item.status || null,
  startDateUtc:   toIsoDateTime(item.startDateUtc),
  expiryDateUtc:  toIsoDateTime(item.expiryDateUtc),
  maxVehicles:    numOrNull(item.maxVehicles),   // '' → null = unlimited
  notes:          item.notes || null,
});

// ─── Signup codes ────────────────────────────────────────────────────────────
// A code gates the creation of a new organization and carries the commercial
// terms with it: which plan, and how many vehicles. Single-use, and redeemed
// rows are the audit trail of who signed up on what terms.
const mapSignupCodeFromApi = (item) => ({
  id:               item.id ?? 0,
  code:             item.code || '',
  planId:           item.planId ?? null,
  planCode:         item.planCode || '',
  planName:         item.planName || '',
  maxVehicles:      item.maxVehicles ?? null,   // null = unlimited, never 0
  note:             item.note || '',
  expiresAtUtc:     item.expiresAtUtc || null,  // null = never expires
  redeemedAtUtc:    item.redeemedAtUtc || null,
  redeemedByUserId: item.redeemedByUserId ?? null,
  isRedeemed:       !!item.isRedeemed,          // server-computed
  createDate:       item.createDate || null,
});

// Form values arrive as strings; '' has to survive as null so the server reads
// it as "use the plan default" / "never expires" rather than as a literal 0.
const mapSignupCodeGenerateToApi = (item) => ({
  planId:       numOrNull(item.planId),
  maxVehicles:  numOrNull(item.maxVehicles),
  note:         (item.note || '').trim() || null,
  expiresAtUtc: toIsoDateTime(item.expiresAtUtc),
  count:        numOrNull(item.count) ?? 1,
});

const createCrud = (createPath, updatePath, deletePath, toApi, fromApi, getListPath) => ({
  /* `?? item` mirrors update() below. postJson returns null for an empty
     response body, which several endpoints send on success — and fromApi(null)
     throws on item.id, so a CREATE THAT SUCCEEDED was reported to the user as
     a failure, and they made the record twice. */
  create: async (item, organizationId, token) => {
    const response = await postJson(createPath, toApi(item, organizationId), token);
    return fromApi(response ?? item);
  },
  update: async (item, organizationId, token) => {
    const response = await postJson(updatePath, toApi(item, organizationId), token);
    return fromApi(response ?? item);
  },
  delete: async (id, token) => postJson(deletePath, { id }, token),
  getAll: getListPath
    ? async (organizationId, token) =>
        toArr(await postJson(getListPath, { id: organizationId }, token)).map(fromApi)
    : null,
});

export const fleetApi = {
  auth: {
    login: (payload) => postJson('/api/Auth/login', payload),
  },
  // These bypass postJson because the upload body is multipart — setting
  // Content-Type by hand would clobber the boundary the browser generates.
  // Everything about the *error* path must still match postJson: a bare Error
  // carries no .status, so normalizeApiError read every failure here as a
  // network outage and printed "Service unavailable" — including 401s (which
  // never ended the session) and the storage-quota 402.
  documents: {
    upload: async (file, token) => {
      const path = '/api/Documents';
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: authHeader(token),
        body: form,
      });
      if (!res.ok) {
        handle401(res.status);
        const errBody = await parseErrorBody(res);
        throw new ApiError(`${res.status}`, res.status, path, errBody);
      }
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
    getById: async (id, token) => {
      const path = `/api/Documents/${id}`;
      const res = await fetch(`${API_BASE}${path}`, {
        headers: authHeader(token),
      });
      if (!res.ok) {
        handle401(res.status);
        const errBody = await parseErrorBody(res);
        throw new ApiError(`${res.status}`, res.status, path, errBody);
      }
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
    // Every caller already treats a failed cleanup as non-blocking, so throwing
    // here is safe — and the previous silence hid 401s that should end the session.
    delete: async (id, token) => {
      const path = `/api/Documents/${id}`;
      const res = await fetch(`${API_BASE}${path}`, {
        method: 'DELETE',
        headers: authHeader(token),
      });
      if (!res.ok) {
        handle401(res.status);
        const errBody = await parseErrorBody(res);
        throw new ApiError(`${res.status}`, res.status, path, errBody);
      }
      return null;
    },
  },
  users: {
    // Passthrough — the caller builds the payload. `signupCode` is required
    // when organizationId is absent or 0 (self-service registration creates a
    // new organization); it is ignored entirely when joining an existing one.
    // The backend answers 400 with one deliberate message for missing / wrong /
    // expired / already-used, so it cannot be probed for which codes exist.
    //
    // `organizationId` is membership only (OrganizationUsers). Role rows carry
    // fleet/zone scope and nothing else — the UserRoles.OrganizationId column
    // was dropped — so never send organizationId expecting it to land on a role.
    create: (payload, token) => postJson('/api/UserLogin/create', payload, token),
    getById: (id, token) => postJson('/api/UserLogin/get-by-id', { id }, token),
    // Assign a user's role together with every fleet and zone it is scoped to.
    // Full replace: each call deletes and re-inserts this user's role rows, so
    // the payload must carry the complete set the user should end up with —
    // anything omitted is dropped, not left alone.
    // Payload: { userId, roleIds: [n], organizationId, fleetIds: [], zoneIds: [] }
    // `organizationId` (> 0) ensures the user is a member of that organization;
    // it is not stored on the role rows. Zero and absent are ignored.
    setUserRoles: (payload, token) => postJson('/api/UserLogin/setUserRoles', payload, token),
    getListByOrganizationId: (organizationId, token) => postJson('/api/UserLogin/getListByOrganizationId', { id: organizationId }, token),
    // Every user across every organization. Takes no id — the backend resolves
    // the caller from the token. SuperAdmin only; returns 403 otherwise.
    getAllUsers: (token) => postJson('/api/UserLogin/getAllUsers', {}, token).then(r => toArr(r).map(mapUserFromApi)),
    updateProfile: async (item, token) => postJson('/api/UserLogin/update', {
      id: item.id || 0,
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email || null,
      companyName: item.companyName || null,
      phoneNumber: item.phoneNumber || null,
      mobilePhoneNumber: item.mobilePhoneNumber || null,
      statusId: item.statusId ?? 1,
    }, token),
    changePassword: async (payload, token) => {
      const res = await fetch(`${API_BASE}/api/UserLogin/changePassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader(token) },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || 'Change password failed');
      return text; // plain text response — not JSON
    },
    updateStatus: async (id, statusId, token) => {
      // Fetch full user first — the update endpoint requires all fields
      const fullUser = await postJson('/api/UserLogin/get-by-id', { id }, token);
      const res = await fetch(`${API_BASE}/api/UserLogin/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader(token) },
        body: JSON.stringify({
          id,
          firstName:         fullUser?.firstName         || '',
          lastName:          fullUser?.lastName          || '',
          companyName:       fullUser?.companyName       || null,
          phoneNumber:       fullUser?.phoneNumber       || null,
          mobilePhoneNumber: fullUser?.mobilePhoneNumber || null,
          statusId,
        }),
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || 'Update status failed');
      return text;
    },
    updateUserStatus: (id, statusId, token) =>
      postJson('/api/UserLogin/updateUserStatus', { userId: id, statusId }, token),
    delete: (id, token) => postJson('/api/UserLogin/delete', { id }, token),
    mapDetailsToUser: mapUserLoginDetailsToUser,
  },
  bootstrap: {
    loadByOrganization: async (organizationId, token) => {
      const [contacts, drivers, fleets, places, vehicles, vendors] = await Promise.all([
        postJson('/api/Contact/GetListByOrganizationId', { id: organizationId }, token),
        postJson('/api/Driver/getListByOrganiztionId', { id: organizationId }, token),
        postJson('/api/Fleet/GetListByOrganizationId', { id: organizationId }, token),
        postJson('/api/Place/GetListByOrganizationId', { id: organizationId }, token),
        postJson('/api/Vehicle/GetListByOrganizationId', { id: organizationId }, token),
        postJson('/api/Vendor/GetListByOrganizationId', { id: organizationId }, token),
      ]);

      return {
        /* `toArr`, not `|| []`. These six endpoints can answer with a
           {$values: […]} or {data: […]} wrapper — that is why every other list
           path in this file goes through toArr. A wrapper is truthy and not an
           array, so `.map` threw during the app's startup load: a blank app,
           not a degraded page. */
        contacts: toArr(contacts).map(mapContactFromApi),
        drivers: toArr(drivers).map(mapDriverFromApi),
        fleets: toArr(fleets).map(mapFleetFromApi),
        places: toArr(places).map(mapPlaceFromApi),
        vehicles: toArr(vehicles).map(mapVehicleFromApi),
        vendors: toArr(vendors).map(mapVendorFromApi),
      };
    },
  },
  contacts: createCrud('/api/Contact/create', '/api/Contact/update', '/api/Contact/delete', mapContactToApi, mapContactFromApi, '/api/Contact/GetListByOrganizationId'),
  drivers: {
    ...createCrud('/api/Driver/create', '/api/Driver/update', '/api/Driver/delete', mapDriverToApi, mapDriverFromApi, '/api/Driver/getListByOrganiztionId'),
    getById: (id, token) => postJson('/api/Driver/getDriverById', { id }, token).then(mapDriverFromApi),
  },
  fleets:   createCrud('/api/Fleet/create',   '/api/Fleet/update',   '/api/Fleet/delete',   mapFleetToApi,   mapFleetFromApi,   '/api/Fleet/GetListByOrganizationId'),
  places:   createCrud('/api/Place/create',   '/api/Place/update',   '/api/Place/delete',   mapPlaceToApi,   mapPlaceFromApi,   '/api/Place/GetListByOrganizationId'),
  /**
   * Geofences and their crossing history.
   *
   * Reading the list is open to any org member; create, update, setEnabled and
   * delete are admin-only and answer 403 otherwise.
   *
   * `update` REPLACES all three link lists with exactly what it is sent, and
   * answers with an empty body — so callers must send the complete lists, never
   * a delta, and must refetch rather than trust the return value.
   *
   * `delete` takes the fence's links, state and event history with it.
   */
  geofences: {
    ...createCrud('/api/Geofence/create', '/api/Geofence/update', '/api/Geofence/delete', mapGeofenceToApi, mapGeofenceFromApi, '/api/Geofence/GetListByOrganizationId'),
    getById:    (id, token)        => postJson('/api/Geofence/getGeofenceById', { id }, token).then(mapGeofenceFromApi),
    /** Flips one or more switches without resending the whole fence. */
    setEnabled: (id, flags, token) => postJson('/api/Geofence/setEnabled', { id, ...onlyBooleans(flags) }, token),
    /** Crossings for one fence, newest first. */
    getEvents:        (id, range, token) =>
      postJson('/api/Geofence/getEventsByGeofenceId', geofenceRangeBody(id, range), token)
        .then((res) => toArr(res).map(mapGeofenceEventFromApi)),
    /** Crossings for one vehicle, across every fence. */
    getVehicleEvents: (id, range, token) =>
      postJson('/api/Geofence/getEventsByVehicleId', geofenceRangeBody(id, range), token)
        .then((res) => toArr(res).map(mapGeofenceEventFromApi)),
  },
  vehicles: {
    ...createCrud('/api/Vehicle/create', '/api/Vehicle/update', '/api/Vehicle/delete', mapVehicleToApi, mapVehicleFromApi, '/api/Vehicle/GetListByOrganizationId'),
    getById: (id, token) => postJson('/api/Vehicle/getVehicleById', { id }, token).then(mapVehicleFromApi),
    /**
     * Immobilize a vehicle through its tracker, or restore it.
     *
     * Admin only. `password` is the signed-in user's own login password, and
     * is REQUIRED when cutting and ignored when restoring — a forgotten
     * password must never be what strands a driver in a dead vehicle, so the
     * restore path deliberately has no credential to get wrong.
     *
     * `force` overrides the server's stationary check and must only be sent
     * after the user has read the 409 reason and explicitly chosen to.
     *
     * A 202 means Traccar accepted the command for delivery — NOT that the
     * engine stopped. The tracker may have no relay wired to the ignition
     * circuit at all. Callers confirm from telemetry; see utils/engineCut.js
     * for the failure classification, which normalizeApiError cannot do here.
     *
     * No skipSessionCheck: a 401 from this endpoint really is a dead session
     * (a wrong password comes back as 400), so the normal logout must run.
     */
    setEngineCut: (vehicleId, cut, { password, force = false } = {}, token) =>
      postJson('/api/Vehicle/EngineCut', { vehicleId, cut, password, force }, token),
  },
  vehicleTrackers: {
    getAll:        (token) => getJson('/api/Vehicle/GetVehicleTrackerNumbers', token).then(res => toArr(res).map(mapVehicleTrackerFromApi)),
    updateExpiry:  (vehicleId, expirationDate, token) => postJson('/api/Vehicle/UpdateTrackerNumberExpirationDate', { vehicleId, expirationDate }, token),
  },
  vendors:   createCrud('/api/Vendor/create',   '/api/Vendor/update',   '/api/Vendor/delete',   mapVendorToApi,   mapVendorFromApi,   '/api/Vendor/GetListByOrganizationId'),
  customers: createCrud('/api/Customer/create', '/api/Customer/update', '/api/Customer/delete', mapCustomerToApi, mapCustomerFromApi, '/api/Customer/GetListByOrganizationId'),
  orders: {
    ...createCrud('/api/Order/create', '/api/Order/update', '/api/Order/delete', mapOrderToApi, mapOrderFromApi, '/api/Order/GetListByOrganizationId'),
    /* THIS BINDING WAS MISSING AND THREE CALLERS ALREADY DEPENDED ON IT.
       `createCrud` returns create/update/delete/getAll only, so every
       `api.orders.getById(...)` — the grid's `commitOrder`, the edit form and
       the status-advance handler — was throwing "is not a function" before any
       request went out. The grid's inline saves failed outright; the two write
       paths could not do the fetch-merge that `Order/update` requires, because
       update REPLACES the record and nulls every field absent from the body.
       The endpoint has existed all along: OrderController.GetById, POST
       /api/Order/getOrderById, taking { id }. Same shape as SellOrder. */
    getById: (id, token) => postJson('/api/Order/getOrderById', { id }, token).then(mapOrderFromApi),
  },
  transactions: createCrud('/api/Transaction/create', '/api/Transaction/update', '/api/Transaction/delete', mapTransactionToApi, mapTransactionFromApi, '/api/Transaction/GetListByOrganizationId'),
  items: {
    ...createCrud('/api/Item/create', '/api/Item/update', '/api/Item/delete', mapItemToApi, mapItemFromApi, '/api/Item/GetListByOrganizationId'),
    getById: (id, token) => postJson('/api/Item/get-by-id', { id }, token).then(mapItemFromApi),
  },
  sellOrders: {
    ...createCrud('/api/SellOrder/create', '/api/SellOrder/update', '/api/SellOrder/delete', mapSellOrderToApi, mapSellOrderFromApi, '/api/SellOrder/GetListByOrganizationId'),
    getById: (id, token) => postJson('/api/SellOrder/getSellOrderById', { id }, token).then(mapSellOrderFromApi),
    // Full-object update: the backend update endpoint replaces the whole
    // record, so sending only {id, statusId} wipes every other field
    // (including organizationId — the order then vanishes from all lists).
    complete: async (id, organizationId, token) => {
      const fresh = await postJson('/api/SellOrder/getSellOrderById', { id }, token).then(mapSellOrderFromApi);
      // Completing again would book the revenue transaction and decrement the
      // van stock a second time (the server has no idempotency guard), so an
      // order that is already completed is returned as-is.
      if (fresh.status === 'completed') return fresh;
      const payload = { ...fresh, status: 'completed', completedAt: new Date().toISOString() };
      const response = await postJson('/api/SellOrder/update', mapSellOrderToApi(payload, organizationId ?? fresh.organizationId), token);
      return response ? mapSellOrderFromApi(response) : payload;
    },
  },

  // ── Currencies ────────────────────────────────────────────────────────
  currencies: {
    getAll:  (orgId, token)    => postJson('/api/Currency/GetListByOrganizationId', { id: orgId }, token).then(r => toArr(r).map(mapCurrencyFromApi)),
    update:  (payload, organizationId, token) => postJson('/api/Currency/update', mapCurrencyToApi(payload, organizationId), token).then(r => r ? mapCurrencyFromApi(r) : payload),
    /* `/api/Currency/setBase` DOES NOT EXIST — the route is `set-default`, and
       it takes a CurrencyCodeRequest (a CODE and an organization id), not an
       id. Two mismatches, and the only caller swallows failures, so setting the
       base currency has silently never worked. */
    setBase: (code, organizationId, token) =>
      postJson('/api/Currency/set-default', { code, organizationId }, token)
        .then(r => (r ? mapCurrencyFromApi(r) : null)),
  },

  // ── Zones ─────────────────────────────────────────────────────────────
  zones: {
    /* Unwrapped HERE rather than in the query hook. This backend sometimes
       wraps a list as `{ $values: [...] }` — the .NET reference-preserving
       serialiser does it whenever the payload contains a cycle — and seven
       call sites had each grown their own copy of the same guard, one of them
       inside a `useMemo` that runs during render. `toArr` is the project's
       answer to that shape, and putting it on the binding means the next
       consumer never has to know. The existing per-page guards keep working;
       they just never fire now. */
    getList: (orgId, token)    => postJson('/api/Zone/GetListByOrganizationId', { id: orgId }, token).then(toArr),
    create:  (payload, token)  => postJson('/api/Zone/create', payload, token),
    update:  (payload, token)  => postJson('/api/Zone/update', payload, token),
    delete:  (id, token)       => postJson('/api/Zone/delete', { id }, token),
  },

  // ── Driver ↔ Vehicle assignments ──────────────────────────────────────
  driverVehicles: {
    getByDriverId: (driverId, token)              => postJson('/api/Driver/getDriverVehiclesByDriverId', { id: driverId }, token),
    create:        (driverId, vehicleId, token)   => postJson('/api/Driver/createDriverVehicle',         { driverId, vehicleId }, token),
    update:        (id, driverId, vehicleId, token) => postJson('/api/Driver/updateDriverVehicle',       { id, driverId, vehicleId }, token),
    delete:        (id, token)                    => postJson('/api/Driver/deleteDriverVehicle',         { id }, token),
  },

  // ── Organizations ─────────────────────────────────────────────────────
  organizations: {
    getAll:  (userId, token)        => postJson('/api/Organization/getListByUserId', { id: userId }, token).then(r => (Array.isArray(r) ? r : []).map(mapOrganizationFromApi)),
    // Every organization, not just the caller's. Takes no id — the backend
    // resolves the caller from the token. SuperAdmin only; 403 otherwise.
    getAllGlobal: (token)           => postJson('/api/Organization/getAll', {}, token).then(r => toArr(r).map(mapOrganizationFromApi)),
    create:  (payload, userId, token) => postJson('/api/Organization/create', mapOrganizationToApi(payload, userId), token).then(mapOrganizationFromApi),
    update:  (payload, userId, token) => postJson('/api/Organization/update', mapOrganizationToApi(payload, userId), token).then(r => r ? mapOrganizationFromApi(r) : payload),
    delete:  (id, token)            => postJson('/api/Organization/delete', { id }, token),
  },

  // ── RBAC ─────────────────────────────────────────────────────────────
  // Single endpoint: returns all views accessible to the given user.
  viewManagement: {
    getViewsByUserId:  (userId, token)          =>
      postJson('/api/ViewManagment/GetViewsByUserId', { id: userId }, token),
    assignViewsToUser: (userId, viewIds, token) =>
      postJson('/api/ViewManagment/AssignViewsToUser', { userId, viewIds }, token),
    // `permissions` (optional): [{ viewId, canAdd, canEdit, canDelete }].
    // Left out, the backend keeps each view's current flags (new view → all true).
    setUserViews: (userId, viewIds, token, permissions) =>
      postJson('/api/UserLogin/setUserViews',
        Array.isArray(permissions) ? { userId, viewIds, permissions } : { userId, viewIds }, token),
  },
  roles: {
    getAll: (token) => getJson('/api/RoleManagment/GetAllRoles', token),
    getByUserId: (userId, token) => postJson('/api/RoleManagment/GetRolesByUserId', { id: userId }, token),
    create:   (name, token)            => postJson('/api/RoleManagment/CreateRole', { name }, token),
    update:   (id, name, token)        => postJson('/api/RoleManagment/UpdateRole', { id, name }, token),
    delete:   (id, token)              => postJson('/api/RoleManagment/DeleteRole', { id }, token),
    setViews: (roleId, viewIds, token) => postJson('/api/RoleManagment/SetRoleViews', { roleId, viewIds }, token),
  },
  views: {
    getAll:     (token)       => getJson('/api/View/getAll', token).then(r => (Array.isArray(r) ? r : []).map(mapViewFromApi)),
    getById:    (id, token)   => postJson('/api/View/getViewById', { id }, token).then(mapViewFromApi),
    getByRoleId:(roleId,token)=> postJson('/api/RoleManagment/GetRoleViewByRoleId', { id: roleId }, token),
    create:     (data, token) => postJson('/api/View/create', mapViewToApi(data), token).then(mapViewFromApi),
    update:     (data, token) => postJson('/api/View/update', mapViewToApi(data), token).then(r => r ? mapViewFromApi(r) : data),
    delete:     (id, token)   => postJson('/api/View/delete', { id }, token),
  },
  // ── Notifications ─────────────────────────────────────────────────────
  notifications: {
    getUnreadCount: (token) => getJson('/api/Notifications/unread-count', token),
    getList: (skip, take, token) => getJson(`/api/Notifications?skip=${skip}&take=${take}`, token),
    markRead:    (id, token) => postJson(`/api/Notifications/${id}/read`, {}, token),
    deleteNotif: (id, token) => deleteJson(`/api/Notifications/${id}`, token),
    sendAnnouncement: (payload, token) => postJson('/api/Notifications/announcement', payload, token),
  },

  // Role assignment itself lives on users.setUserRoles — one full-replace call
  // that covers the role and its fleet/zone scope together.
  userRoles: {
    getByRoleId: (roleId, token) => postJson('/api/UserRole/getByRoleId', { roleId }, token),
  },

  orderTemplates: {
    getList:    (userId, token) => postJson('/api/OrderTemplate/GetListByUserId',      { id: userId }, token).then(r => (Array.isArray(r) ? r : []).map(mapTemplateFromApi)),
    getDefault: (userId, token) => postJson('/api/OrderTemplate/GetDefaultByUserId',   { id: userId }, token).then(r => r ? mapTemplateFromApi(r) : null),
    getById:    (id,     token) => postJson('/api/OrderTemplate/getOrderTemplateById', { id },     token).then(mapTemplateFromApi),
    create:     (data,   token) => postJson('/api/OrderTemplate/add',    mapTemplateToApi(data), token).then(mapTemplateFromApi),
    update:     (data,   token) => postJson('/api/OrderTemplate/edit',   mapTemplateToApi(data), token).then(r => r ? mapTemplateFromApi(r) : data),
    delete:     (id,     token) => postJson('/api/OrderTemplate/delete', { id }, token),
  },

  traccar: {
    getPositionByUniqueId: (uniqueId, password, token) =>
      getJson(
        `/integrations/traccar/positions/by-unique-id/${encodeURIComponent(uniqueId)}?password=${encodeURIComponent(password)}`,
        token
      ),
    getRoutePointsByUniqueId: (payload, token) =>
      postJson('/integrations/traccar/route-points/by-unique-id', payload, token),
  },

  // ── Document Types ───────────────────────────────────────────────��────
  documentType: {
    getAll:  (token)       => getJson('/api/DocumentType/getAll', token).then(r => toArr(r).map(mapDocumentTypeFromApi)),
    save:    (item, token) => postJson('/api/DocumentType/save',   mapDocumentTypeToApi(item), token).then(mapDocumentTypeFromApi),
    delete:  (id, token)   => postJson('/api/DocumentType/delete', { id }, token),
  },

  // ── Default Required Document Templates ───────────────────────────────
  defaultResourceDocuments: {
    getList:  (orgId, token) => getJson(`/api/DefaultResourceDocument/getList?organizationId=${orgId}`, token).then(r => toArr(r).map(mapDefaultResourceDocFromApi)),
    save:     (item, token)  => postJson('/api/DefaultResourceDocument/save',   mapDefaultResourceDocToApi(item), token).then(r => r ? mapDefaultResourceDocFromApi(r) : item),
    add:      (item, token)  => postJson('/api/DefaultResourceDocument/add',    mapDefaultResourceDocToApi(item), token).then(mapDefaultResourceDocFromApi),
    delete:   (id, token)    => postJson('/api/DefaultResourceDocument/delete', { id }, token),
  },

  // ── Per-Resource Required Documents ───────────────────────────────────
  resourceDocuments: {
    getByResource:  (resourceId, resourceType, token) => postJson('/api/ResourceDocument/getByResource',  { resourceId, resourceType }, token).then(r => toArr(r).map(mapResourceDocFromApi)),
    add:            (item, token)                     => postJson('/api/ResourceDocument/add',            mapResourceDocToApi(item), token).then(r => r ? mapResourceDocFromApi(r) : item),
    save:           (item, token)                     => postJson('/api/ResourceDocument/save',           mapResourceDocToApi(item), token).then(r => r ? mapResourceDocFromApi(r) : item),
    delete:         (id, token)                       => postJson('/api/ResourceDocument/delete',         { resourceDocumentId: id }, token),
    ensureDefaults: (resourceType, resourceId, token) => postJson('/api/ResourceDocument/ensureDefaultDocuments', { resourceType, resourceId }, token),
  },

  // ── Vehicle Repairs ───────────────────────────────────────────────────
  repairs: {
    getAll:       (orgId, token)  => postJson('/api/Repair/GetListByOrganizationId', { id: orgId }, token).then(r => toArr(r).map(mapRepairFromApi)),
    getByVehicle: (vid, token)    => postJson('/api/Repair/GetListByVehicleId',      { id: vid }, token).then(r => toArr(r).map(mapRepairFromApi)),
    getById:      (id, token)     => postJson('/api/Repair/GetById',                 { id }, token).then(mapRepairFromApi),
    create:       (item, _, tok)  => postJson('/api/Repair/create',                  mapRepairToApi(item), tok).then(mapRepairFromApi),
    update:       (item, _, tok)  => postJson('/api/Repair/update',                  mapRepairToApi(item), tok),
    delete:       (id, token)     => postJson('/api/Repair/delete',                  { id }, token),
  },

  // ── Vehicle Loads (inventory) ──────────────────────────────────────────
  // Order-driven stock deductions happen backend-side on order creation;
  // adjust is only for manual moves (Load onto vehicle, Restock to warehouse).
  vehicleLoads: {
    adjust: ({ vehicleId, itemId, quantity, type }, token) =>
      postJson('/api/VehicleLoad/Adjust', { vehicleId, itemId, quantity, type }, token).then(r => r ? mapVehicleLoadFromApi(r) : null),
    getByOrganizationId: (orgId, token)     => postJson('/api/VehicleLoad/GetListByOrganizationId', { id: orgId }, token).then(r => toArr(r).map(mapVehicleLoadFromApi)),
    getByVehicleId:      (vehicleId, token) => postJson('/api/VehicleLoad/GetListByVehicleId', { id: vehicleId }, token).then(r => toArr(r).map(mapVehicleLoadFromApi)),
    getById:             (id, token)        => postJson('/api/VehicleLoad/GetById', { id }, token).then(mapVehicleLoadFromApi),
    getTransactionsByLoadId: (loadId, token) =>
      postJson('/api/VehicleLoad/GetTransactionsByVehicleLoadId', { id: loadId }, token).then(r => toArr(r).map(mapVehicleLoadTransactionFromApi)),
    getTransactionsByVehicleAndRange: (vehicleId, from, to, token) =>
      postJson('/api/VehicleLoad/GetTransactionsByVehicleIdAndDateRange', { vehicleId, from, to }, token).then(r => toArr(r).map(mapVehicleLoadTransactionFromApi)),
  },

  // ── Settings (Default = system-wide, User = per-user override) ────────
  settings: {
    getUserSettings:    (userId, token) => postJson('/api/Setting/GetUserSettings', { id: userId }, token).then(r => toArr(r).map(mapSettingFromApi)),
    editUserSetting:    (id, value, token) => postJson('/api/Setting/EditUserSetting', { id, value: String(value) }, token).then(r => r ? mapSettingFromApi(r) : null),
    getDefaultSettings: (token)        => postJson('/api/Setting/GetDefaultSettings', {}, token).then(r => toArr(r).map(mapSettingFromApi)),
    addDefaultSetting:  (item, token)  => postJson('/api/Setting/AddDefaultSetting',  mapDefaultSettingToApi(item), token).then(mapSettingFromApi),
    editDefaultSetting: (item, token)  => postJson('/api/Setting/EditDefaultSetting', mapDefaultSettingToApi(item), token).then(r => r ? mapSettingFromApi(r) : item),
    deleteDefaultSetting: (id, token)  => postJson('/api/Setting/DeleteDefaultSetting', { id }, token),
  },

  // ── Vehicle Assets ────────────────────────────────────────────────────
  vehicleAssets: {
    getByVehicle: (vid, token)    => postJson('/api/VehicleAsset/GetListByVehicleId', { id: vid }, token).then(r => toArr(r).map(mapVehicleAssetFromApi)),
    getById:      (id, token)     => postJson('/api/VehicleAsset/getById',             { id }, token).then(mapVehicleAssetFromApi),
    create:       (item, _, tok)  => postJson('/api/VehicleAsset/create',              mapVehicleAssetToApi(item), tok).then(mapVehicleAssetFromApi),
    update:       (item, _, tok)  => postJson('/api/VehicleAsset/update',              mapVehicleAssetToApi(item), tok),
    delete:       (id, token)     => postJson('/api/VehicleAsset/delete',              { id }, token),
  },

  // ── Fuel ────────────────────────────────────────────────────────────────
  fuel: {
    getByVehicle:        (vehicleId, from, to, token) => postJson('/api/Fuel/GetListByVehicleId',       { id: vehicleId, from, to }, token).then(r => toArr(r).map(mapFuelEntryFromApi)),
    getByOrganizationId: (orgId, from, to, token)      => postJson('/api/Fuel/GetListByOrganizationId',  { id: orgId,     from, to }, token).then(r => toArr(r).map(mapFuelEntryFromApi)),
    getById:             (id, token)                   => postJson('/api/Fuel/GetById',                  { id }, token).then(mapFuelEntryFromApi),
    create:              (item, _, tok)                => postJson('/api/Fuel/create',                   mapFuelEntryToApi(item), tok).then(mapFuelEntryFromApi),
    update:              (item, _, tok)                => postJson('/api/Fuel/update',                   mapFuelEntryToApi(item), tok).then(mapFuelEntryFromApi),
    delete:              (id, token)                   => postJson('/api/Fuel/delete',                   { id }, token),

    // Reports
    getConsumptionByVehicle:            (vehicleId, from, to, token) => postJson('/api/Fuel/GetConsumptionByVehicleId',           { id: vehicleId, from, to }, token).then(mapFuelConsumptionReportFromApi),
    getSummaryByOrganizationId:         (orgId, from, to, token)     => postJson('/api/Fuel/GetSummaryByOrganizationId',          { id: orgId,     from, to }, token).then(r => toArr(r).map(mapVehicleFuelSummaryFromApi)),
    getEstimateByVehicle:               (vehicleId, from, to, token) => postJson('/api/Fuel/GetEstimateByVehicleId',              { id: vehicleId, from, to }, token).then(r => r ? mapFuelEstimateFromApi(r) : null),
    getEstimateSummaryByOrganizationId: (orgId, from, to, token)     => postJson('/api/Fuel/GetEstimateSummaryByOrganizationId',  { id: orgId,     from, to }, token).then(r => toArr(r).map(mapFuelEstimateFromApi)),
    getOdometerLogByVehicle:            (vehicleId, from, to, token) => postJson('/api/Fuel/GetOdometerLogByVehicleId',           { id: vehicleId, from, to }, token).then(r => toArr(r).map(mapOdometerLogFromApi)),
  },

  // ── My Plan (any authenticated user) ────────────────────────────────────
  // Takes no organization id — the backend resolves the org from the token.
  subscriptions: {
    getMyPlan: (token) => postJson('/api/Subscription/GetMyPlan', {}, token).then(r => (r ? mapMyPlanFromApi(r) : null)),
  },

  // ── Plan administration (SuperAdmin — backend returns 403 otherwise) ─────
  planAdmin: {
    getPlans:           (token)                   => postJson('/api/PlanAdmin/plans/list',            {}, token).then(r => toArr(r).map(mapPlanFromApi)),
    upsertPlan:         (plan, token)             => postJson('/api/PlanAdmin/plans/upsert',          mapPlanToApi(plan), token).then(r => (r ? mapPlanFromApi(r) : plan)),
    deletePlan:         (id, token)               => postJson('/api/PlanAdmin/plans/delete',          { id }, token),
    getPlanViews:       (id, token)               => postJson('/api/PlanAdmin/plans/getViews',        { id }, token).then(r => toArr(r).map(mapViewFromApi)),
    // Full replace, not a delta — send every view id the plan should have.
    setPlanViews:       (planId, viewIds, token)  => postJson('/api/PlanAdmin/plans/setViews',        { planId, viewIds: (viewIds || []).map(Number) }, token),
    getSubscriptions:   (token)                   => postJson('/api/PlanAdmin/subscriptions/list',    {}, token).then(r => toArr(r).map(mapSubscriptionFromApi)),
    upsertSubscription: (sub, token)              => postJson('/api/PlanAdmin/subscriptions/upsert',  mapSubscriptionToApi(sub), token).then(r => (r ? mapSubscriptionFromApi(r) : sub)),
    cancelSubscription: (id, token)               => postJson('/api/PlanAdmin/subscriptions/cancel',  { id }, token),

    // ── Signup codes ──────────────────────────────────────────────────────
    // generate answers with an array even when count is 1.
    generateSignupCodes: (form, token) =>
      postJson('/api/PlanAdmin/signupCodes/generate', mapSignupCodeGenerateToApi(form), token)
        .then(r => toArr(r).map(mapSignupCodeFromApi)),
    getSignupCodes:      (token) =>
      postJson('/api/PlanAdmin/signupCodes/list', {}, token)
        .then(r => toArr(r).map(mapSignupCodeFromApi)),
    // 204 on success; 404 for an unknown id; 400 when the code was already
    // redeemed — those rows are the audit record and cannot be removed.
    revokeSignupCode:    (id, token) =>
      postJson('/api/PlanAdmin/signupCodes/revoke', { id }, token),
  },
};
